import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { GerinusBD } from 'src/app/app-core/lib/gerinus-bd.component';
import { GlobalService } from 'src/app/app-core/lib/global.service';
import { ProcessService } from 'src/app/app-core/services/process.service';
import { DocumentSignatureService } from 'src/app/app-core/services/document-signature.service';
import { UploadService } from 'src/app/app-core/services/upload.service';
import { environment } from 'src/environments/environment';
import { SignedFileDataResponse } from 'src/app/interfaces/signature-gov.interfaces';

interface ISecretaryParecerFilters {
    numeroProcesso: string;
    interessado: string;
    ementa: string;
    dataRange: Date[] | undefined;
}

@Component({
    selector: 'app-secretary-pending-signatures',
    templateUrl: './secretary-pending-signatures.component.html',
    styleUrls: ['./secretary-pending-signatures.component.scss'],
    providers: [MessageService, ProcessService, DocumentSignatureService, UploadService],
})
export class SecretaryPendingSignaturesComponent extends GerinusBD implements OnInit, OnDestroy {
    public customTitle: string = 'Assinaturas do Secretário';

    public isFiltersModalOpen: boolean = false;
    public isFiltersModalResponsiveOpen: boolean = false;
    public filtersModel: ISecretaryParecerFilters = this.defaultFilters();
    public appliedFilters: ISecretaryParecerFilters = this.defaultFilters();

    public isSigning: boolean = false;
    public signingMessage: string = '';
    public signingParecerId: number | null = null;

    public isParecerViewerOpen: boolean = false;
    public isParecerViewerLoading: boolean = false;
    public parecerViewerUrl: SafeResourceUrl | null = null;
    public parecerViewerTitle: string = 'Visualizar Parecer';
    private parecerViewerBlobUrl: string | null = null;

    private destroy$ = new Subject<void>();
    private popupPollInterval: any = null;
    private boundMessageListener: ((event: MessageEvent) => void) | null = null;
    private pendingSignContext:
        | {
              parecerId: number;
              pdfBase64: string;
              fileName: string;
              pageCount: number;
              signaturePosition: { page: number; xPercentage: number; yPercentage: number } | null;
          }
        | null = null;

    constructor(
        public global: GlobalService,
        public processService: ProcessService,
        private documentSignatureService: DocumentSignatureService,
        private uploadService: UploadService,
        private sanitizer: DomSanitizer,
        private messageService: MessageService,
        private ngZone: NgZone,
    ) {
        super(global, processService);
        this.showPaginate = true;
        this.showEmptyData = true;
        this.emptyTitle = 'Nenhum parecer encontrado';
        this.emptyText = 'Não há pareceres para os filtros e situação selecionados.';
    }

    public emptyTitle: string;
    public emptyText: string;

    ngOnInit(): void {
        this.listAll();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.clearPopupPoll();
    }

    public listAll(page: number = 1): void {
        const userId = this.global.user()?.USR_CodigoUsuario;
        if (!userId) {
            (this as any).list = [];
            return;
        }

        (this as any).list = null;
        (this as any).pagination = null;

        const filters = this.serializeFilters(this.appliedFilters);
        this.processService
            .listParecerAllForSecretary(userId, page, 10, filters)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    (this as any).list = response?.data || [];
                    (this as any).pagination = response?.meta || null;
                    this.updateEmptyTexts();
                },
                error: () => {
                    (this as any).list = [];
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Não foi possível carregar os pareceres.',
                    });
                },
            });
    }

    public isPending(item: any): boolean {
        return item?.PPD_StatusRascunho === 'AGUARDANDO_SECRETARIO';
    }

    public applyFilters(): void {
        this.appliedFilters = { ...this.filtersModel };
        this.isFiltersModalOpen = false;
        this.isFiltersModalResponsiveOpen = false;
        this.listAll(1);
    }

    public clearFilters(): void {
        this.filtersModel = this.defaultFilters();
        this.appliedFilters = this.defaultFilters();
        this.isFiltersModalOpen = false;
        this.isFiltersModalResponsiveOpen = false;
        this.listAll(1);
    }

    public get hasActiveFilters(): boolean {
        return !!(
            this.appliedFilters.numeroProcesso ||
            this.appliedFilters.interessado ||
            this.appliedFilters.ementa ||
            (this.appliedFilters.dataRange && this.appliedFilters.dataRange[0])
        );
    }

    private defaultFilters(): ISecretaryParecerFilters {
        return {
            numeroProcesso: '',
            interessado: '',
            ementa: '',
            dataRange: undefined,
        };
    }

    private serializeFilters(filters: ISecretaryParecerFilters) {
        const result: { numeroProcesso?: string; interessado?: string; ementa?: string; dataInicio?: string; dataFim?: string } = {};
        if (filters.numeroProcesso) result.numeroProcesso = filters.numeroProcesso;
        if (filters.interessado) result.interessado = filters.interessado;
        if (filters.ementa) result.ementa = filters.ementa;
        if (filters.dataRange && filters.dataRange[0]) {
            result.dataInicio = this.toIsoDate(filters.dataRange[0], false);
        }
        if (filters.dataRange && filters.dataRange[1]) {
            result.dataFim = this.toIsoDate(filters.dataRange[1], true);
        }
        return result;
    }

    private toIsoDate(date: Date, endOfDay: boolean): string {
        const d = new Date(date);
        if (endOfDay) {
            d.setHours(23, 59, 59, 999);
        } else {
            d.setHours(0, 0, 0, 0);
        }
        return d.toISOString();
    }

    private updateEmptyTexts(): void {
        this.emptyTitle = 'Nenhum parecer encontrado';
        this.emptyText = 'Não há pareceres pendentes ou assinados para os filtros selecionados.';
    }

    public openParecer(item: any): void {
        if (!item?.PPD_Anexo) return;

        this.parecerViewerTitle = item.PRC_NumeroProcesso
            ? `Parecer — ${item.PRC_NumeroProcesso}`
            : 'Visualizar Parecer';
        this.revokeParecerViewerBlobUrl();
        this.parecerViewerUrl = null;
        this.isParecerViewerLoading = true;
        this.isParecerViewerOpen = true;

        this.uploadService
            .download(item.PPD_Anexo, 'SIGN')
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: any) => {
                    if (!response?.url) {
                        this.isParecerViewerLoading = false;
                        this.closeParecerViewer();
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Erro',
                            detail: 'Não foi possível obter o PDF do parecer.',
                        });
                        return;
                    }

                    this.uploadService
                        .downloadAndReturnArrayBuffer(response.url)
                        .pipe(takeUntil(this.destroy$), finalize(() => (this.isParecerViewerLoading = false)))
                        .subscribe({
                            next: (buffer: ArrayBuffer) => {
                                const blob = new Blob([buffer], { type: 'application/pdf' });
                                const blobUrl = URL.createObjectURL(blob);
                                this.parecerViewerBlobUrl = blobUrl;
                                this.parecerViewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
                            },
                            error: () => {
                                this.closeParecerViewer();
                                this.messageService.add({
                                    severity: 'error',
                                    summary: 'Erro',
                                    detail: 'Falha ao carregar o PDF do parecer.',
                                });
                            },
                        });
                },
                error: () => {
                    this.isParecerViewerLoading = false;
                    this.closeParecerViewer();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Falha ao obter o PDF do parecer.',
                    });
                },
            });
    }

    public closeParecerViewer(): void {
        this.isParecerViewerOpen = false;
        this.parecerViewerUrl = null;
        this.isParecerViewerLoading = false;
        this.revokeParecerViewerBlobUrl();
    }

    private revokeParecerViewerBlobUrl(): void {
        if (this.parecerViewerBlobUrl) {
            try {
                URL.revokeObjectURL(this.parecerViewerBlobUrl);
            } catch {
                /* noop */
            }
            this.parecerViewerBlobUrl = null;
        }
    }

    public sign(item: any): void {
        if (this.isSigning) return;
        this.isSigning = true;
        this.signingParecerId = item.PPD_CodigoParecerDespacho;
        this.signingMessage = 'Gerando PDF para assinatura...';

        this.processService
            .prepareParecerSecretarySign(item.PPD_CodigoParecerDespacho)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: ({ pdfBase64, fileName, pageCount, signaturePosition }) => {
                    this.signingMessage = 'Aguardando assinatura no gov.br...';
                    this.openGovBrPopup(item.PPD_CodigoParecerDespacho, pdfBase64, fileName, pageCount, signaturePosition);
                },
                error: (err) => {
                    this.endSigningFlow();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: err?.error?.message || 'Falha ao preparar PDF para assinatura.',
                    });
                },
            });
    }

    private endSigningFlow(): void {
        this.isSigning = false;
        this.signingMessage = '';
        this.signingParecerId = null;
    }

    private openGovBrPopup(
        parecerId: number,
        pdfBase64: string,
        fileName: string,
        pageCount: number,
        signaturePosition: { page: number; xPercentage: number; yPercentage: number } | null,
    ): void {
        const signUrl = `${environment.govBR.signUrl}/authorize?response_type=code&redirect_uri=${encodeURIComponent(
            environment.govBR.returnSignUrl,
        )}&scope=sign&client_id=${environment.govBR.signClientID}`;
        const width = 800;
        const height = 600;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        const popup = window.open(signUrl, '_blank', `width=${width},height=${height},top=${top},left=${left}`);

        this.pendingSignContext = { parecerId, pdfBase64, fileName, pageCount, signaturePosition };
        this.registerPostMessageFallback();

        const POLL_TIMEOUT_MS = 5 * 60 * 1000;
        const startedAt = Date.now();

        this.popupPollInterval = setInterval(() => {
            if (!popup || popup.closed) {
                this.clearPopupPoll();
                this.ngZone.run(() => {
                    if (this.pendingSignContext) {
                        this.pendingSignContext = null;
                        this.endSigningFlow();
                        this.messageService.add({
                            severity: 'warn',
                            summary: 'Assinatura cancelada',
                            detail: 'A janela de assinatura foi fechada antes da conclusão.',
                        });
                    }
                });
                return;
            }

            if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
                try { popup.close(); } catch { /* noop */ }
                this.clearPopupPoll();
                this.ngZone.run(() => {
                    this.pendingSignContext = null;
                    this.endSigningFlow();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Tempo esgotado',
                        detail: 'A assinatura não foi concluída a tempo. Tente novamente.',
                    });
                });
                return;
            }

            try {
                if (popup.location.href.includes('code=')) {
                    const url = new URL(popup.location.href);
                    const code = url.searchParams.get('code');
                    popup.close();
                    this.clearPopupPoll();
                    this.ngZone.run(() => {
                        if (code && this.pendingSignContext) {
                            const ctx = this.pendingSignContext;
                            this.pendingSignContext = null;
                            this.callSignApi(code, ctx.parecerId, ctx.pdfBase64, ctx.fileName, ctx.pageCount, ctx.signaturePosition);
                        } else {
                            this.endSigningFlow();
                        }
                    });
                }
            } catch {
                // cross-origin while popup is on gov.br domain — wait until same-origin redirect
            }
        }, 1000);
    }

    private registerPostMessageFallback(): void {
        if (this.boundMessageListener) return;

        this.boundMessageListener = (event: MessageEvent) => {
            try {
                const returnUrlOrigin = new URL(environment.govBR.returnSignUrl).origin;
                if (event.origin !== returnUrlOrigin) return;
                const code = (event.data && (event.data.code || event.data.signCode)) as string | undefined;
                if (!code || !this.pendingSignContext) return;
                const ctx = this.pendingSignContext;
                this.pendingSignContext = null;
                this.clearPopupPoll();
                this.ngZone.run(() => this.callSignApi(code, ctx.parecerId, ctx.pdfBase64, ctx.fileName, ctx.pageCount, ctx.signaturePosition));
            } catch {
                /* noop */
            }
        };
        window.addEventListener('message', this.boundMessageListener);
    }

    private callSignApi(
        code: string,
        parecerId: number,
        pdfBase64: string,
        fileName: string,
        pageCount: number,
        computedPosition: { page: number; xPercentage: number; yPercentage: number } | null,
    ): void {
        const lastPageIndex = Math.max(0, (pageCount || 1) - 1);
        const signaturePosition = computedPosition || { page: lastPageIndex, xPercentage: 50, yPercentage: 5 };
        this.signingMessage = 'Assinando documento...';
        this.documentSignatureService
            .signatureAndSaveDocument({
                code,
                fileName,
                pdfBase64,
                signaturePosition,
            })
            .subscribe(
                (response: any) => {
                    const signed = response as SignedFileDataResponse;
                    this.signingMessage = 'Finalizando assinatura...';
                    this.finalizeSign(parecerId, signed.fileCloudFlare, fileName);
                },
                () => {
                    this.endSigningFlow();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Falha na assinatura via gov.br.',
                    });
                },
            );
    }

    private finalizeSign(parecerId: number, fileCloudFlare: string, fileName: string): void {
        const userId = this.global.user()?.USR_CodigoUsuario;
        this.processService
            .finalizeParecerSecretarySign(parecerId, {
                fileCloudFlare,
                fileName: `${fileName}.pdf`,
                userId,
            })
            .pipe(takeUntil(this.destroy$), finalize(() => this.endSigningFlow()))
            .subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        icon: 'ph ph-check-circle',
                        closable: true,
                        summary: 'Sucesso!',
                        detail: 'Parecer assinado com sucesso.',
                    });
                    this.listAll((this.pagination as any)?.current_page || 1);
                },
                error: (err) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: err?.error?.message || 'Não foi possível finalizar a assinatura.',
                    });
                },
            });
    }

    private clearPopupPoll(): void {
        if (this.popupPollInterval) {
            clearInterval(this.popupPollInterval);
            this.popupPollInterval = null;
        }
        if (this.boundMessageListener) {
            window.removeEventListener('message', this.boundMessageListener);
            this.boundMessageListener = null;
        }
    }
}
